import { createSlice, createAsyncThunk } from '@reduxjs/toolkit';
import api from '../../api/axios';

/** Normalize payload: sidebar passes a string; full page may pass { message, conversationId } */
const getSendPayload = (payload) => {
  if (typeof payload === 'string') {
    return { text: payload.trim(), conversationId: undefined };
  }
  const text = payload?.message?.trim?.() ?? '';
  return {
    text,
    conversationId: payload?.conversationId
  };
};

/** Backend errors arrive as { error } (rate limiter, controller) or { message } — read both */
const extractError = (error, fallback) =>
  error.response?.data?.message || error.response?.data?.error || fallback;

// Helper: remove numbered/bulleted list lines that duplicate job cards when jobs are present
const removeRedundantJobLines = (text = '', jobs = []) => {
  if (!Array.isArray(jobs) || jobs.length === 0) return text;

  const jobTokens = jobs
    .map((j) => ({
      title: (j?.title || j?.jobPosition || j?.job_title || '').toString().toLowerCase().trim(),
      company: (j?.company || j?.companyName || j?.company?.name || '').toString().toLowerCase().trim()
    }))
    .filter((t) => t.title || t.company);

  if (!jobTokens.length) return text;

  const lines = String(text || '').split('\n');
  const filtered = lines.filter((line) => {
    const v = line.toString().toLowerCase().trim();
    if (!v) return false; // drop empty lines

    const looksLikeList = /^\s*(?:\d+[\.\)]|[-*•])\s+/.test(v);
    if (!looksLikeList) return true; // keep non-list lines

    // If the line mentions a known job title or company, drop it (redundant)
    const matchesJob = jobTokens.some(
      (t) => (t.title && v.includes(t.title)) || (t.company && v.includes(t.company))
    );

    return !matchesJob;
  });

  return filtered.join('\n').trim();
};

// Sanitize only — never replace or de-format the assistant's text. Markdown
// is rendered by the chat bubbles; here we only drop list lines that would
// duplicate the job cards shown below the message.
const sanitizeAssistantContent = (text = '', jobs = []) => removeRedundantJobLines(String(text ?? '').trim(), jobs);

/**
 * Send a message with a streamed (SSE) response. Dispatches streamDelta as
 * tokens arrive so the UI can render text live; resolves with the same final
 * payload shape as the JSON endpoint. Falls back to the plain JSON endpoint
 * only when the stream request could not be established at all (so the
 * message is never processed twice).
 */
export const sendMessage = createAsyncThunk(
  'aiChat/sendMessage',
  async (payload, { getState, dispatch, rejectWithValue }) => {
    const token = localStorage.getItem('token');

    if (!token) {
      console.error('❌ No authentication token found. Please log in.');
      return rejectWithValue('Please log in to use the chatbot');
    }

    const { text, conversationId: idFromPayload } = getSendPayload(payload);
    if (!text) {
      return rejectWithValue('Message is required');
    }

    const { conversationId: stateConversationId } = getState().aiChat;
    const conversationId = idFromPayload ?? stateConversationId;
    const body = JSON.stringify({ message: text, conversationId });

    // ── Try the streaming endpoint first ────────────────────────────────
    let streamEstablished = false;
    try {
      const baseURL = api.defaults.baseURL || '';
      const resp = await fetch(`${baseURL}/api/chatbot/message/stream`, {
        method: 'POST',
        credentials: 'include',
        headers: {
          'Content-Type': 'application/json',
          'auth-token': token,
          Authorization: `Bearer ${token}`
        },
        body
      });

      if (resp.ok && resp.body) {
        // Once the server accepted the request it WILL process the message —
        // from here on we must not fall back (that would double-send).
        streamEstablished = true;

        const reader = resp.body.getReader();
        const decoder = new TextDecoder();
        let buffer = '';
        let donePayload = null;

        for (;;) {
          const { value, done } = await reader.read();
          if (done) break;
          buffer += decoder.decode(value, { stream: true });

          let idx;
          while ((idx = buffer.indexOf('\n\n')) >= 0) {
            const frame = buffer.slice(0, idx);
            buffer = buffer.slice(idx + 2);
            const line = frame.split('\n').find((l) => l.startsWith('data:'));
            if (!line) continue;

            let evt;
            try { evt = JSON.parse(line.slice(5).trim()); } catch { continue; }

            if (evt.type === 'delta' && evt.text) {
              dispatch(streamDelta(evt.text));
            } else if (evt.type === 'done') {
              donePayload = evt;
            } else if (evt.type === 'error') {
              return rejectWithValue(evt.error || 'Failed to send message');
            }
          }
        }

        if (donePayload) return donePayload;
        return rejectWithValue('Connection interrupted — please try again');
      }
      // Non-OK response (e.g. 404 on an old backend, 429) → try JSON endpoint
    } catch (streamErr) {
      if (streamEstablished) {
        return rejectWithValue('Connection interrupted — please try again');
      }
      // network/CORS failure before the server accepted → safe to fall back
    }

    // ── Fallback: plain JSON endpoint ────────────────────────────────────
    try {
      const response = await api.post('/api/chatbot/message', {
        message: text,
        conversationId
      });
      return response.data;
    } catch (error) {
      return rejectWithValue(extractError(error, 'Failed to send message'));
    }
  }
);

// Async thunk to list the user's conversations (history panel)
export const getConversations = createAsyncThunk(
  'aiChat/getConversations',
  async (_, { rejectWithValue }) => {
    try {
      const response = await api.get('/api/chatbot/conversations');

      return response.data;
    } catch (error) {
      return rejectWithValue(extractError(error, 'Failed to load conversations'));
    }
  }
);

// Async thunk to delete a specific conversation from history
export const deleteConversation = createAsyncThunk(
  'aiChat/deleteConversation',
  async (conversationId, { rejectWithValue }) => {
    try {
      await api.delete(`/api/chatbot/history/${conversationId}`);

      return { conversationId };
    } catch (error) {
      return rejectWithValue(extractError(error, 'Failed to delete conversation'));
    }
  }
);

// Async thunk to get conversation history
export const getHistory = createAsyncThunk(
  'aiChat/getHistory',
  async (conversationId, { rejectWithValue }) => {
    try {
      const response = await api.get(`/api/chatbot/history/${conversationId}`);

      return response.data;
    } catch (error) {
      return rejectWithValue(extractError(error, 'Failed to get history'));
    }
  }
);

// Async thunk to clear history
export const clearHistory = createAsyncThunk(
  'aiChat/clearHistory',
  async (_, { getState, rejectWithValue }) => {
    try {
      const { conversationId } = getState().aiChat;

      if (!conversationId) {
        return { success: true };
      }

      const response = await api.delete(`/api/chatbot/history/${conversationId}`);

      return response.data;
    } catch (error) {
      return rejectWithValue(extractError(error, 'Failed to clear history'));
    }
  }
);

const aiChatSlice = createSlice({
  name: 'aiChat',
  initialState: {
    isOpen: false,
    conversationId: null,
    messages: [],
    isLoading: false,
    error: null,
    currentJobs: [],
    hasMore: false,
    metadata: {},
    followUpQuestion: null,
    conversations: [],
    conversationsLoading: false,
    streamingText: '',
    lastFailedMessage: null
  },
  reducers: {
    streamDelta: (state, action) => {
      state.streamingText = (state.streamingText || '') + action.payload;
    },
    openChat: (state) => {
      state.isOpen = true;
    },
    closeChat: (state) => {
      state.isOpen = false;
    },
    toggleChat: (state) => {
      state.isOpen = !state.isOpen;
    },
    clearError: (state) => {
      state.error = null;
    },
    newConversation: (state) => {
      state.conversationId = null;
      state.messages = [];
      state.isLoading = false;
      state.error = null;
      state.currentJobs = [];
      state.hasMore = false;
      state.metadata = {};
      state.followUpQuestion = null;
      state.streamingText = '';
      state.lastFailedMessage = null;
    }
  },
  extraReducers: (builder) => {
    builder
      // Send message
      .addCase(sendMessage.pending, (state, action) => {
        state.isLoading = true;
        state.error = null;
        state.streamingText = '';
        state.lastFailedMessage = null;
        state.followUpQuestion = null;

        const { text } = getSendPayload(action.meta?.arg);
        if (!text) return;

        state.messages.push({
          role: 'user',
          content: text,
          timestamp: new Date().toISOString()
        });
      })
      .addCase(sendMessage.fulfilled, (state, action) => {
        state.isLoading = false;
        state.streamingText = '';

        // Add AI response (sanitize assistant content before storing)
        const payload = action.payload;
        const response = payload.message || payload.data?.response;
        const conversationId = payload.conversationId || payload.data?.conversationId;
        const metadata = payload.metadata || payload.data?.metadata || {};
        const jobs = metadata.jobs || payload.data?.jobs || [];
        const followUpQuestion = payload.followUpQuestion;

        const cleaned = sanitizeAssistantContent(response, jobs);

        state.messages.push({
          role: 'assistant',
          content: cleaned,
          jobs,
          metadata: {
            ...metadata,
            jobs,
            hasMore: metadata.hasMore || false
          }
        });

        if (conversationId) {
          state.conversationId = conversationId;
        }

        // Keep the history panel in sync: move/insert the active conversation at the top
        if (state.conversationId) {
          const firstUserMsg = state.messages.find((m) => m.role === 'user');
          const existingIdx = state.conversations.findIndex(
            (c) => c.conversationId === state.conversationId
          );
          const entry = {
            conversationId: state.conversationId,
            preview: existingIdx >= 0 ? state.conversations[existingIdx].preview : (firstUserMsg?.content || 'New conversation'),
            messageCount: state.messages.length,
            lastMessageAt: new Date().toISOString()
          };
          if (existingIdx >= 0) state.conversations.splice(existingIdx, 1);
          state.conversations.unshift(entry);
        }

        if (jobs.length > 0) {
          state.currentJobs = jobs;
        }

        state.metadata = {
          ...metadata,
          jobs,
          hasMore: metadata.hasMore || false
        };
        // Store follow-up question
        state.followUpQuestion = followUpQuestion || null;
      })
      .addCase(sendMessage.rejected, (state, action) => {
        state.isLoading = false;
        state.streamingText = '';
        state.error = action.payload || 'Failed to send message';
        const { text } = getSendPayload(action.meta?.arg);
        state.lastFailedMessage = text || null;
        // Pop the optimistic user bubble — the error banner + Retry represent
        // the failed send, and retrying would otherwise duplicate it.
        const last = state.messages[state.messages.length - 1];
        if (text && last && last.role === 'user' && last.content === text) {
          state.messages.pop();
        }
      })
      // Get history
      .addCase(getHistory.pending, (state) => {
        state.isLoading = true;
        state.error = null;
      })
      .addCase(getHistory.fulfilled, (state, action) => {
        state.isLoading = false;
        const payloadData = action.payload?.data || action.payload || {};
        const raw = payloadData.messages || [];
        state.messages = raw.map((msg) => {
          if (msg?.role !== 'assistant') return msg;
          const msgJobs = msg?.metadata?.jobs || msg?.jobs || [];
            return {
              ...msg,
              content: sanitizeAssistantContent(msg.content, msgJobs)
            };
        });
        if (payloadData.conversationId) {
          state.conversationId = payloadData.conversationId;
        }
      })
      .addCase(getHistory.rejected, (state, action) => {
        state.isLoading = false;
        state.error = action.payload || 'Failed to load history';
      })
      // Clear history (deletes the active conversation server-side)
      .addCase(clearHistory.pending, (state) => {
        state.isLoading = true;
        state.error = null;
      })
      .addCase(clearHistory.fulfilled, (state) => {
        state.isLoading = false;
        state.conversations = state.conversations.filter(
          (c) => c.conversationId !== state.conversationId
        );
        state.messages = [];
        state.conversationId = null;
        state.currentJobs = [];
        state.metadata = {};
        state.followUpQuestion = null;
      })
      .addCase(clearHistory.rejected, (state, action) => {
        state.isLoading = false;
        state.error = action.payload || 'Failed to clear history';
      })
      // List conversations
      .addCase(getConversations.pending, (state) => {
        state.conversationsLoading = true;
      })
      .addCase(getConversations.fulfilled, (state, action) => {
        state.conversationsLoading = false;
        state.conversations = action.payload?.conversations || [];
      })
      .addCase(getConversations.rejected, (state) => {
        state.conversationsLoading = false;
      })
      // Delete a conversation from history
      .addCase(deleteConversation.fulfilled, (state, action) => {
        const { conversationId } = action.payload;
        state.conversations = state.conversations.filter(
          (c) => c.conversationId !== conversationId
        );
        if (state.conversationId === conversationId) {
          state.messages = [];
          state.conversationId = null;
          state.currentJobs = [];
          state.metadata = {};
          state.followUpQuestion = null;
        }
      })
      .addCase(deleteConversation.rejected, (state, action) => {
        state.error = action.payload || 'Failed to delete conversation';
      });
  }
});

export const { openChat, closeChat, toggleChat, clearError, newConversation, streamDelta } = aiChatSlice.actions;

export default aiChatSlice.reducer;
